import { useEffect, useState } from 'react';
import './AgencyCodeSelector.scss';
import { useUserData } from 'Foundation/User/UserContext';
import { IconAccordionCaretDown, IconButton } from '@lmig/lmds-react-icons';
import { Popover } from '@lmig/lmds-react-popover';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import { StringHelper } from 'Utilities/StringHelper';
import 'Feature/Chrome/PageHeaderDropdown.scss';

export function AgencyCodeSelector() {
  const [statCodes, setStatCodes] = useState<Agency[]>();
  const [filteredCodes, setFilteredCodes] = useState<Agency[]>();
  const [selectedStatCode, setSelectedStatCode] = useState<string>();
  const { userData, updateSelectedStat } = useUserData();
  const [isOpen, setIsOpen] = useState<boolean>();
  const [enabled, setIsenabled] = useState<boolean>();
  const [searchValue, setSearchValue] = useState<string>();

  useEffect(() => {
    if (userData?.selectedStatCodeData.SubStat)
      setSelectedStatCode(StringHelper.formatStatCode(userData.selectedStatCodeData.SubStat));
  }, [userData?.selectedStatCodeData.SubStat]);

  useEffect(() => {
    async function getAgencies() {
      let res = await fetch('/api/sitecore/Agency/GetAgencies');
      let data = await res.json();
      if (data && data.length > 0)
        setStatCodes(
          data
            .filter((y: MasterAgency) => y.Subs.length > 0 || y.HasAccess)
            .flatMap((x: MasterAgency) => [{ AgencyNumber: x.AgencyNumber, HasAccess: x.HasAccess }, ...x.Subs])
            .sort((w: Agency, z: Agency) => parseInt(w.AgencyNumber) - parseInt(z.AgencyNumber))
            .map((x: Agency) => ({
              AgencyNumber: StringHelper.formatStatCode(x.AgencyNumber),
              HasAccess: x.HasAccess != undefined ? x.HasAccess : true,
            }))
            .filter((z: Agency) => z.HasAccess),
        );
    }
    if (!statCodes) getAgencies();
  }, []);

  useEffect(() => {
    if (statCodes && statCodes.length > 1) setIsenabled(true);
  }, [statCodes]);

  useEffect(() => {
    if (searchValue && statCodes) {
      let filterValue = searchValue.replaceAll('-', '');
      setFilteredCodes(statCodes.filter((z) => z.AgencyNumber.replaceAll('-', '').indexOf(filterValue) > -1));
    } else setFilteredCodes(undefined);
  }, [searchValue]);

  const onStatSelected = async (event: React.MouseEvent<HTMLElement>, formattedSubStat: string) => {
    event.stopPropagation();
    setSelectedStatCode(formattedSubStat);
    setIsOpen(false);
    try {
      const res = await updateSelectedStat(formattedSubStat);
      if (res !== '') {
        window.location.reload();
      }
    } catch {
      console.error("Error saving user's agency code");
    }
  };

  const onOpen = () => enabled && setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const highlight = (agencyCode: string) => {
    if (searchValue) {
      let filterValue = searchValue.replaceAll('-', '');
      let match = agencyCode.match(
        filterValue
          .split('')
          .map((x) => x + '-?')
          .join(''),
      );
      if (match) {
        let index: number = match.index ?? 0;
        let length: number = match[0].length ?? 0;
        return (
          <span>
            {agencyCode.substring(0, index)}
            <b>{agencyCode.substring(index, index + length)}</b>
            {agencyCode.substring(index + length)}
          </span>
        );
      }
    }
    return <span>{agencyCode}</span>;
  };

  //only show the search filter when there are 6 or more subs or masters
  const showFilter = () => statCodes && statCodes.length >= 4;

  return (
    <div className="top-nav-dropdown">
      <Popover
        onOpen={() => onOpen()}
        onClose={() => onClose()}
        trigger={
          <IconButton background="transparent" className={enabled ? '' : 'agency-code-disabled'}>
            <div className="icon-button-label">
              <span>{selectedStatCode}</span>
              {enabled && <IconAccordionCaretDown title="" />}
            </div>
          </IconButton>
        }
        variant="custom"
        direction="below"
        align="start"
        className={isOpen ? 'open' : 'closed'}
      >
        <div className="popover-content">
          {showFilter() && (
            <SearchInput
              id="agency-code-filter"
              labelVisual="Filter codes"
              value={searchValue}
              filter={new RegExp('^(?:\\d[-]*){0,8}$')}
              onChange={(e) => setSearchValue(e.target.value)}
              aria-live="polite"
              onClearClicked={() => setSearchValue('')}
            />
          )}
          <ul>
            {(filteredCodes ?? statCodes) &&
              (filteredCodes ?? statCodes)?.map((x) => (
                <li onClick={(event) => x.HasAccess && onStatSelected(event, x.AgencyNumber)}>
                  <div className={`master-code ${x.HasAccess ? '' : 'no-access'}`}>
                    <span>{highlight(x.AgencyNumber)}</span>
                  </div>
                </li>
              ))}
          </ul>
          {filteredCodes && filteredCodes.length == 0 && (
            <p className="no-matching-agency-codes">No matching agency codes</p>
          )}
        </div>
      </Popover>
    </div>
  );
}
